import apiClient from './client';

// ------------------------------------------------------------------
// 五维配置 —— 知识库（Knowledge）
// 复用主平台端点 + 练玉坊文档上传（multipart）：
//   GET    /api/v1/agents/{agent_id}/knowledge
//   POST   /api/v1/agents/{agent_id}/knowledge
//   POST   /api/v1/agents/{agent_id}/knowledge/search
//   DELETE /api/v1/agents/{agent_id}/knowledge/{item_id}
//   PUT    /api/v1/knowledge/{item_id}
//   POST   /api/v1/playground/agents/{id}/knowledge/upload（练玉坊文档上传）
// ------------------------------------------------------------------

export interface KnowledgeItem {
  id: string;
  agent_id?: string;
  /** 兼容字段：后端无 title 列，展示时用内容首行派生 */
  title?: string;
  content?: string;
  source?: string;
  source_type?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
}

export interface CreateKnowledgeInput {
  /** 标题（可选）：入库时拼到内容首行，便于列表展示与检索 */
  title?: string;
  content?: string;
  source?: string;
  source_type?: string;
  metadata?: Record<string, unknown>;
}

export interface KnowledgeSearchInput {
  query: string;
  top_k?: number;
}

export interface KnowledgeSearchResult {
  item: KnowledgeItem;
  score?: number;
}

export async function listKnowledge(agentId: string): Promise<KnowledgeItem[]> {
  const res = await apiClient.get<KnowledgeItem[]>(`/agents/${agentId}/knowledge`);
  return res.data;
}

/**
 * 新建知识条目。走 agent 作用域端点（后端校验 Agent 写权限并按需补建 Project
 * 镜像行）；模型无 title 列，标题拼入内容首行。
 */
export async function createKnowledge(agentId: string, input: CreateKnowledgeInput): Promise<KnowledgeItem> {
  const title = (input.title || '').trim();
  const content = (input.content || '').trim();
  const full = title && !content.startsWith(title) ? `${title}\n${content}`.trim() : (content || title);
  const res = await apiClient.post<KnowledgeItem>(`/agents/${agentId}/knowledge`, {
    content: full,
    source_type: input.source_type || 'manual',
  });
  return res.data;
}

export async function searchKnowledge(
  agentId: string,
  input: KnowledgeSearchInput
): Promise<KnowledgeSearchResult[]> {
  const res = await apiClient.post<KnowledgeSearchResult[]>(
    `/agents/${agentId}/knowledge/search`,
    input
  );
  return res.data;
}

export async function updateKnowledge(itemId: string, input: Partial<CreateKnowledgeInput>): Promise<KnowledgeItem> {
  const res = await apiClient.put<KnowledgeItem>(`/knowledge/${itemId}`, input);
  return res.data;
}

export async function deleteKnowledge(agentId: string, itemId: string): Promise<void> {
  await apiClient.delete(`/agents/${agentId}/knowledge/${itemId}`);
}

export interface UploadKnowledgeResult {
  created: number;
  filename: string;
}

/**
 * 上传文档解析入库（练玉坊端点）：multipart FormData，字段名 file。
 * 支持 docx/pdf/txt/md，≤10MB；失败由后端返回 422 中文 detail。
 */
export async function uploadKnowledgeDocument(
  agentId: string,
  file: File
): Promise<UploadKnowledgeResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiClient.post<UploadKnowledgeResult>(
    `/playground/agents/${agentId}/knowledge/upload`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data;
}
